import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { businessInfo } from '../mock/mockData';

export const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    garageSize: '',
    serviceType: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Mock form submission
    console.log('Form submitted:', formData);
    
    toast({
      title: "Quote Request Received!",
      description: "We'll contact you within 24 hours to schedule your free estimate.",
    });

    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      garageSize: '',
      serviceType: '',
      message: ''
    });
  };

  return (
    <section id="contact" className="py-20 bg-slate-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-slate-900 mb-4">Get Your Free Quote</h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Fill out the form below and we'll contact you within 24 hours
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-12 max-w-6xl mx-auto">
          {/* Contact Information */}
          <div className="lg:col-span-1 space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-slate-900 mb-6">Contact Information</h3>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-100 rounded-lg p-3">
                    <Phone className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Phone</p>
                    <a
                      href={`tel:${businessInfo.phoneRaw}`}
                      className="text-lg font-semibold text-slate-900 hover:text-blue-600 transition-colors"
                    >
                      {businessInfo.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-blue-100 rounded-lg p-3">
                    <Mail className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Email</p>
                    <a
                      href={`mailto:${businessInfo.email}`}
                      className="text-lg font-semibold text-slate-900 hover:text-blue-600 transition-colors"
                    >
                      {businessInfo.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-blue-100 rounded-lg p-3">
                    <MapPin className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Location</p>
                    <p className="text-lg font-semibold text-slate-900">{businessInfo.location}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-blue-100 rounded-lg p-3">
                    <Clock className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Business Hours</p>
                    <p className="text-lg font-semibold text-slate-900">{businessInfo.hours}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-blue-600 rounded-xl p-6 text-white">
              <h4 className="text-lg font-bold mb-4">Why Choose Us?</h4>
              <ul className="space-y-3 text-sm">
                <li>✓ Same-week service available</li>
                <li>✓ Free on-site estimates</li>
                <li>✓ Fully insured & licensed</li>
                <li>✓ Eco-friendly disposal</li>
                <li>✓ 100% satisfaction guarantee</li>
              </ul>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-2">
                    Full Name *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    className="w-full"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
                    Email Address *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    className="w-full"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-slate-700 mb-2">
                    Phone Number *
                  </label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(519) 123-4567"
                    className="w-full"
                  />
                </div>

                <div>
                  <label htmlFor="garageSize" className="block text-sm font-semibold text-slate-700 mb-2">
                    Garage Size
                  </label>
                  <select
                    id="garageSize"
                    name="garageSize"
                    value={formData.garageSize}
                    onChange={handleChange}
                    className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select size</option>
                    <option value="single">Single Car</option>
                    <option value="double">2-Car Garage</option>
                    <option value="triple">3+ Car Garage</option>
                  </select>
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="serviceType" className="block text-sm font-semibold text-slate-700 mb-2">
                  Service Interested In
                </label>
                <select
                  id="serviceType"
                  name="serviceType"
                  value={formData.serviceType}
                  onChange={handleChange}
                  className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a package</option>
                  <option value="bronze">Bronze - Basic Cleanout ($299-$399)</option>
                  <option value="silver">Silver - Standard Organization ($499-$699)</option>
                  <option value="gold">Gold - Complete Transformation ($899-$1,299)</option>
                  <option value="platinum">Platinum - Premium Service ($1,500-$2,500+)</option>
                  <option value="alacarte">À La Carte Services</option>
                  <option value="commercial">Commercial/Real Estate</option>
                </select>
              </div>

              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-semibold text-slate-700 mb-2">
                  Tell us about your project
                </label>
                <Textarea
                  id="message"
                  name="message"
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Describe your garage's current condition and what you'd like to achieve..."
                  className="w-full"
                />
              </div>

              <Button
                type="submit"
                size="lg"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white text-lg"
              >
                <Send className="mr-2 h-5 w-5" />
                Request Free Quote
              </Button>

              <p className="text-sm text-slate-500 text-center mt-4">
                We'll respond within 24 hours to schedule your free on-site estimate
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
